/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import { Button, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import TabsNavigation from 'components/TabsNavigation'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { EditorField } from 'components/Editor'
import { InputField, NumberInputField } from 'components/Input'
import { validateRequired, validateRequiredHtml, validateRequiredNumber } from 'utils/validation'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { QuestionInvertTypeEnum } from '__generatedTypes__/globalTypes'
import { Passage } from 'pagesNew/PracticeTests/Form/PassagesFieldArray'
import InvertImagesField from 'pagesNew/Questions/Form/InvertImagesField'
import * as classes from './styles'

export type GREPassagesFieldArrayProps = {
  fields: FieldArrayRenderProps<Passage, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Passage, HTMLElement>['meta']
}

export const GREPassagesFieldArray = ({ fields, meta }: GREPassagesFieldArrayProps) => {
  const [selected, setSelected] = useState<string>('0')
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const arrayError =
    typeof meta.error === 'object' && meta.error && '_error' in meta.error ? meta.error._error : undefined

  const handleAdd = () => {
    fields.push({ invertContentImages: QuestionInvertTypeEnum.AUTO })
    setSelected(String(fields.value?.length ?? 0))
  }

  const handleConfirm = () => {
    if (indexToRemove === null) {
      return
    }

    const nextLastIndex = Math.max(0, (fields.value?.length ?? 1) - 2)

    fields.remove(indexToRemove)

    if (+selected > nextLastIndex) {
      setSelected(String(nextLastIndex))
    }

    setIndexToRemove(null)
  }

  return (
    <>
      <div>
        {!!fields.length && (
          <Typography variant="body2" css={classes.label}>
            Passage #
          </Typography>
        )}
        <div css={classes.tabsRow}>
          {!!fields.length && (
            <TabsNavigation
              tabs={fields.map((field, index) => ({
                id: String(index),
                title: String(index + 1),
                error: !!meta.touched && !!meta.error?.[index],
              }))}
              value={{ id: String(selected), title: String(+selected + 1), error: false }}
              onChange={({ id }) => setSelected(id)}
            />
          )}
          <Button
            variant="outlined"
            color="blue"
            size="small"
            onClick={handleAdd}
            css={classes.addButton}
            data-testid="add-passage"
          >
            Add Passage <AddRoundedIcon />
          </Button>
        </div>
        {!!arrayError && meta.touched && (
          <Typography variant="body2" css={classes.error} data-testid="passages-error">
            {arrayError}
          </Typography>
        )}
      </div>
      <div>
        {fields.map((field, index) => (
          <div key={field} css={classes.tabContent(+selected === index)}>
            <div css={classes.passageHeader}>
              <InputField label="Passage Title" name={`${field}.title`} validate={validateRequired} fullWidth />
              <Button
                variant="outlined"
                color="red"
                size="small"
                onClick={() => setIndexToRemove(index)}
                css={classes.iconButton}
                data-testid={`remove-passage-${index}`}
              >
                <TrashIcon />
              </Button>
            </div>
            <div css={classes.columns}>
              <NumberInputField
                label="Start Question"
                name={`${field}.startQuestion`}
                validate={validateRequiredNumber}
                error={meta.error?.[index]?.startQuestion}
                fullWidth
              />
              <NumberInputField
                label="End Question"
                name={`${field}.endQuestion`}
                validate={validateRequiredNumber}
                error={meta.error?.[index]?.endQuestion}
                fullWidth
              />
            </div>
            <EditorField
              type="passage"
              label="Passage Content"
              name={`${field}.content`}
              placeholder="Enter Passage Content"
              validate={validateRequiredHtml}
            />
            <InvertImagesField
              name={`${field}.invertContentImages`}
              title="Passage"
              validate={validateRequired}
              htmlWithImagesForPreview={fields.value?.[index]?.content}
            />
          </div>
        ))}
      </div>
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting Passage #${(indexToRemove ?? 0) + 1}`}
        subtitle="Are you sure you want to delete this passage?"
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </>
  )
}
